import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { TimeBucket } from '@/api/statistics';
import { useChartTheme } from '@/hooks/useChartTheme';
import { useTranslation } from 'react-i18next';

interface SpendingOverTimeChartProps {
  data: TimeBucket[];
  currency: string;
}

function CustomTooltip({ active, payload, label, currency }: any) {
  if (!active || !payload?.length) return null;
  const minorUnits = getMinorUnitsSync(currency);
  return (
    <div className="rounded-lg bg-surface-primary px-3 py-2 shadow-lg ring-1 ring-border-primary">
      <p className="text-xs font-medium text-text-tertiary">{label}</p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} className="text-sm font-semibold" style={{ color: entry.color }}>
          {entry.name}: {formatMinorAmount(entry.value, currency, minorUnits)}
        </p>
      ))}
    </div>
  );
}

export function SpendingOverTimeChart({ data, currency }: SpendingOverTimeChartProps) {
  const { t } = useTranslation();
  const minorUnits = getMinorUnitsSync(currency);
  const ct = useChartTheme();
  const formatTick = (value: number) =>
    formatMinorAmount(value, currency, minorUnits, undefined, true);

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-text-quaternary">
        {t('web:spendingOverTimeChart.noSpendingDataForThisPeriod')}
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('web:spendingOverTimeChart.spendingOverTime')}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: ct.tick }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: ct.tick }}
            axisLine={false}
            tickLine={false}
            width={80}
            tickFormatter={formatTick}
          />
          <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ stroke: ct.cursorStroke }} />
          <Area
            type="monotone"
            dataKey="youPaid"
            name="You paid"
            stackId="1"
            stroke="#4f46e5"
            fill="#4f46e5"
            fillOpacity={0.6}
          />
          <Area
            type="monotone"
            dataKey="yourShare"
            name="Your share"
            stackId="2"
            stroke="#a5b4fc"
            fill="#a5b4fc"
            fillOpacity={0.4}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
